import { useEffect, useMemo, useState, type JSX } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle } from "@untitledui/icons";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { Modal } from "@/components/application/modals/modal-controlled";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { TextArea } from "@/components/base/textarea/textarea";
import {
  MICROGROUP_DISSOLVE_PHRASE,
  microgroupDissolveSchema,
  type MicrogroupDissolveInput,
} from "../../schemas/microgroupDissolve";

/**
 * UC-006 MicrogroupDissolveConfirmDialog — 3-stupňový Confirm Dialog (Phase 28).
 *
 * Per princip #5 anti-accidental destructive action a UC-006 EC4 (R-040 / F11
 * 48h SLA pro card invalidation).
 *
 * 3 stupně:
 *   1. **Info / důsledky**     — žádný input, jen pokračovat;
 *   2. **Reason**              — textarea min 10 znaků (audit log entry);
 *   3. **Confirm phrase**      — "Napište ZRUŠIT" exact-match (case-sensitive).
 *
 * Esc handling per §5.9.1:
 *   - Stupeň 1 + 3 (s prázdnou phrase) → Esc dismiss directly;
 *   - Stupeň 2 (vyplněný reason) → Esc spustí 2-stage confirm "Opravdu zavřít
 *     a zahodit zapsaný důvod?".
 *   - Stupeň 3 (nevyplněná phrase ale vyplněný reason) → také confirm.
 */

export interface MicrogroupDissolveConfirmDialogProps {
  open: boolean;
  microgroupName: string;
  activeMemberCount: number;
  submitting?: boolean;
  submitError?: string | null;
  onCancel: () => void;
  onConfirm: (input: MicrogroupDissolveInput) => void;
}

type Step = 1 | 2 | 3;

export function MicrogroupDissolveConfirmDialog(
  props: MicrogroupDissolveConfirmDialogProps,
): JSX.Element {
  const {
    open,
    microgroupName,
    activeMemberCount,
    submitting,
    submitError,
    onCancel,
    onConfirm,
  } = props;

  const [step, setStep] = useState<Step>(1);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const form = useForm<MicrogroupDissolveInput>({
    resolver: zodResolver(microgroupDissolveSchema),
    defaultValues: { reason: "", confirmPhrase: "" },
    mode: "onChange",
  });

  const reason = form.watch("reason");
  const confirmPhrase = form.watch("confirmPhrase");

  const hasUserInput = useMemo(
    () => reason.trim().length > 0 || confirmPhrase.length > 0,
    [reason, confirmPhrase],
  );

  // Reset při (re-)open. `form` ref je stabilní z useForm — záměrně mimo deps.
  useEffect(() => {
    if (open) {
      setStep(1);
      setConfirmDiscard(false);
      form.reset({ reason: "", confirmPhrase: "" });
    }
  }, [open, form]);

  function handleDismissAttempt() {
    if (hasUserInput && !confirmDiscard) {
      setConfirmDiscard(true);
      return;
    }
    onCancel();
  }

  // Per-step gating
  const canAdvanceFromStep2 = reason.trim().length >= 10;
  const canSubmit = confirmPhrase === MICROGROUP_DISSOLVE_PHRASE && canAdvanceFromStep2;

  async function handleSubmit() {
    const valid = await form.trigger();
    if (!valid) return;
    onConfirm(form.getValues());
  }

  function renderBody(): JSX.Element {
    if (confirmDiscard) {
      return (
        <InlineAlert variant="warning" title="Opravdu zahodit zapsané údaje?">
          Máte zapsaný důvod nebo potvrzovací frázi — pokud zavřete tento
          dialog, údaje budou zahozeny. Pokračovat?
        </InlineAlert>
      );
    }

    if (step === 1) {
      return (
        <div
          className="microgroup-dissolve__step"
          data-testid="microgroup-dissolve-step-1"
        >
          <p>
            Připravujete se zrušit mikroskupinu
            <strong> {microgroupName}</strong>. Tato akce je nevratná.
          </p>
          <InlineAlert variant="warning" title="Důsledky zrušení">
            <ul className="microgroup-dissolve__consequences">
              <li>
                Všech {activeMemberCount} aktivních členů ztratí přístup —
                budou informováni mimo aplikaci.
              </li>
              <li>
                Tankovací karty členů budou označeny ke{" "}
                <strong>zneplatnění v Axigonu</strong> — Petr má lhůtu
                48&nbsp;h pro provedení zneplatnění.
              </li>
              <li>
                Historické rozpočty a úhrady zůstávají dohledatelné pro
                drill-down.
              </li>
            </ul>
          </InlineAlert>
        </div>
      );
    }

    if (step === 2) {
      return (
        <div
          className="microgroup-dissolve__step"
          data-testid="microgroup-dissolve-step-2"
        >
          <p>
            Uveďte důvod zrušení — text bude zapsán do auditního logu
            a Petr ho uvidí ve frontě ke schválení.
          </p>
          <Controller
            control={form.control}
            name="reason"
            render={({ field, fieldState }) => (
              <TextArea
                label="Důvod zrušení (min 10 znaků)"
                isRequired
                rows={4}
                placeholder="Např. mikroskupina se rozpadla po výpovědi smlouvy s Axigonem…"
                data-testid="microgroup-dissolve-reason"
                name={field.name}
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                isInvalid={!!fieldState.error}
                hint={fieldState.error?.message ?? undefined}
              />
            )}
          />
        </div>
      );
    }

    return (
      <div
        className="microgroup-dissolve__step"
        data-testid="microgroup-dissolve-step-3"
      >
        <p>
          Pro nevratné zrušení napište do políčka přesně frázi
          <strong> „{MICROGROUP_DISSOLVE_PHRASE}"</strong> (velká písmena,
          bez uvozovek).
        </p>
        <Input
          label="Potvrzovací fráze"
          hint={form.formState.errors.confirmPhrase?.message ?? "Diakritika a velká/malá písmena musí sedět."}
          isRequired
          isInvalid={!!form.formState.errors.confirmPhrase}
          value={confirmPhrase}
          onChange={(v) => form.setValue("confirmPhrase", v, { shouldValidate: true })}
          placeholder={MICROGROUP_DISSOLVE_PHRASE}
          autoComplete="off"
          data-testid="microgroup-dissolve-phrase"
        />
        <p
          className="microgroup-dissolve__phrase-hint"
          data-testid="microgroup-dissolve-phrase-status"
        >
          {confirmPhrase === MICROGROUP_DISSOLVE_PHRASE
            ? "✓ Fráze odpovídá."
            : "× Fráze zatím neodpovídá."}
        </p>
        {submitError && (
          <InlineAlert variant="error" title="Chyba při zrušení">
            {submitError}
          </InlineAlert>
        )}
      </div>
    );
  }

  function renderFooter(): JSX.Element {
    if (confirmDiscard) {
      return (
        <>
          <Button color="secondary" onPress={() => setConfirmDiscard(false)}>
            Pokračovat ve vyplňování
          </Button>
          <Button
            color="primary-destructive"
            onPress={() => {
              setConfirmDiscard(false);
              onCancel();
            }}
            data-testid="microgroup-dissolve-discard-confirm"
          >
            Zahodit a zavřít
          </Button>
        </>
      );
    }

    if (step === 1) {
      return (
        <>
          <Button color="secondary" onPress={handleDismissAttempt} isDisabled={submitting}>
            Zrušit
          </Button>
          <Button
            color="primary"
            onPress={() => setStep(2)}
            data-testid="microgroup-dissolve-next-1"
          >
            Rozumím, pokračovat
          </Button>
        </>
      );
    }

    if (step === 2) {
      return (
        <>
          <Button color="secondary" onPress={() => setStep(1)} isDisabled={submitting}>
            Zpět
          </Button>
          <Button
            color="primary"
            onPress={() => setStep(3)}
            isDisabled={!canAdvanceFromStep2}
            data-testid="microgroup-dissolve-next-2"
          >
            Pokračovat
          </Button>
        </>
      );
    }

    return (
      <>
        <Button color="secondary" onPress={() => setStep(2)} isDisabled={submitting}>
          Zpět
        </Button>
        <Button
          color="primary-destructive"
          onPress={handleSubmit}
          isDisabled={!canSubmit || submitting}
          data-testid="microgroup-dissolve-confirm"
        >
          {submitting ? "Ruším…" : "Zrušit mikroskupinu (nevratně)"}
        </Button>
      </>
    );
  }

  return (
    <Modal
      open={open}
      title="Zrušit mikroskupinu"
      icon={AlertTriangle}
      iconColor="error"
      onClose={handleDismissAttempt}
      footer={renderFooter()}
    >
      {renderBody()}
    </Modal>
  );
}
